import { useQuery } from "@tanstack/react-query";
import {
  collection,
  getDocs,
  query,
  QueryConstraint,
  where,
} from "firebase/firestore";
import { topicConverter } from "~/iterators/firebaseConverter/topic";
import { firestore } from "~/iterators/firestore/firestore";
import {
  FIRESTORE_CACHE_REFRESH_MIN_MILL_SEC,
  FIRESTORE_CACHE_DEFAULT_MILL_SEC,
} from "~/lib/constants";
import { Project } from "~/models/project";
import {
  FIRESTORE_COLLECTION_NAME_TOPIC,
  Topic,
  topicDefaultSort,
} from "~/models/topic";

interface Props {
  forceFetch?: boolean;
  projectId?: Project["id"];
}

export function useTopics(props: Props = {}): {
  topics: Topic[] | undefined;
} {
  const { data: topics } = useQuery(
    ["topics", props.projectId],
    () => getTopics(props),
    {
      cacheTime: props.forceFetch
        ? FIRESTORE_CACHE_REFRESH_MIN_MILL_SEC
        : FIRESTORE_CACHE_DEFAULT_MILL_SEC, // Cache retention period
      enabled: true,
      staleTime: FIRESTORE_CACHE_DEFAULT_MILL_SEC, // Time to consider cache stale
      suspense: true,
    }
  );
  return { topics };
}

async function getTopics({ projectId }: Props): Promise<Topic[]> {
  const queryConstraints: QueryConstraint[] = [];
  if (projectId) {
    queryConstraints.push(where("projectId", "==", projectId));
  }
  const snapshot = await getDocs(
    query(
      collection(firestore, FIRESTORE_COLLECTION_NAME_TOPIC).withConverter(
        topicConverter
      ),
      ...queryConstraints
    )
  );

  const topics = [] as Topic[];
  snapshot.forEach((doc) => {
    topics.push(doc.data());
  });

  return topics.sort(topicDefaultSort);
}
